import { useRef, useState } from "react";
import { ArrowUpRight, Minus, Play, Plus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { WHAT_I_DO } from "@/data/content";

export const WhatIDo = () => {
  const [open, setOpen] = useState(null);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const playVideo = () => {
    videoRef.current?.play();
    setPlaying(true);
  };

  return (
    <section data-testid="what-i-do-section" className="noise relative bg-navy-ink py-24 md:py-32">
      <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
        <Reveal>
          <p className="font-serif text-lg italic text-zinc-400">{WHAT_I_DO.kicker}</p>
          <h2 className="mt-4 font-display text-3xl font-bold uppercase leading-tight tracking-tight text-white sm:text-4xl">
            {WHAT_I_DO.headline}
          </h2>
          <div className="mt-6 h-px w-16 bg-navy" />
        </Reveal>

        <Reveal delay={0.1} className="mt-14">
          <div className="mt-4 w-full">
            <div
              className="relative w-full overflow-hidden bg-navy-ink"
              style={{ aspectRatio: "16 / 9", maxHeight: "70vh" }}
            >
              <video
                ref={videoRef}
                data-testid="heritage-video"
                poster="/assets/busy-dog-archival-poster.jpg"
                muted
                playsInline
                preload="metadata"
                controls={playing}
                className="h-full w-full object-cover"
              >
                <source src="/assets/busy-dog-archival-commercial.mp4" type="video/mp4" />
                <source src="/assets/busy-dog-archival-commercial.webm" type="video/webm" />
              </video>
              <div className="archive-scanlines pointer-events-none absolute inset-0" aria-hidden="true" />
              <div
                className="pointer-events-none absolute inset-0"
                aria-hidden="true"
                style={{
                  background:
                    "radial-gradient(ellipse at center, transparent 52%, rgba(5, 7, 11, 0.6) 100%)",
                }}
              />
              {!playing && (
                <button
                  type="button"
                  data-testid="heritage-video-play"
                  onClick={playVideo}
                  aria-label="Play archival 1-800-Busy Dog brand video"
                  className="absolute inset-0 flex items-center justify-center bg-navy-ink/20 transition-colors hover:bg-navy-ink/10"
                >
                  <span className="archive-play flex h-20 w-20 items-center justify-center rounded-full bg-navy text-white md:h-24 md:w-24">
                    <Play size={30} className="ml-1 fill-white" />
                  </span>
                </button>
              )}
            </div>
          </div>

          <p
            data-testid="heritage-video-caption"
            className="mx-auto mt-4 max-w-2xl text-center font-serif text-sm italic text-zinc-500"
          >
            An original 1-800-Busy Dog brand video from the company's early years.
          </p>
        </Reveal>

        <div className="mt-16">
          {WHAT_I_DO.areas.map((area, i) => {
            const isOpen = open === i;
            const flip = i % 2 === 1;
            return (
              <Reveal key={area.num} delay={0.05}>
                <article
                  data-testid={`what-i-do-area-${area.num}`}
                  className={`grid grid-cols-1 gap-8 py-10 md:py-12 lg:grid-cols-12 lg:gap-14 ${
                    i > 0 ? "border-t border-white/10" : "pt-0 md:pt-0"
                  }`}
                >
                  <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
                    <button
                      type="button"
                      data-testid={`what-i-do-trigger-${area.num}`}
                      aria-expanded={isOpen}
                      aria-controls={`what-i-do-panel-${area.num}`}
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="group w-full rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7FA8E8] focus-visible:ring-offset-4 focus-visible:ring-offset-navy-ink"
                    >
                      <div className="flex items-start justify-between gap-6">
                        <div>
                          <span
                            className="font-display text-sm font-semibold"
                            style={{ color: "#7FA8E8" }}
                          >
                            {area.num}
                          </span>
                          <h3 className="mt-3 font-display text-2xl font-bold uppercase leading-tight tracking-tight text-white transition-colors group-hover:text-[#c8dbff] sm:text-3xl">
                            {area.heading}
                          </h3>
                          <p className="mt-4 font-serif text-base italic leading-relaxed text-zinc-400 md:text-lg">
                            {area.short}
                          </p>
                        </div>
                        <span
                          data-testid={`what-i-do-indicator-${area.num}`}
                          className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 text-zinc-300 transition-colors group-hover:border-[#7FA8E8] group-hover:text-[#7FA8E8]"
                        >
                          {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                        </span>
                      </div>
                    </button>
                  </div>

                  <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
                    <div
                      id={`what-i-do-panel-${area.num}`}
                      data-testid={`what-i-do-panel-${area.num}`}
                      role="region"
                      aria-labelledby={`what-i-do-trigger-${area.num}`}
                      aria-hidden={!isOpen}
                      className={`grid transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                        isOpen
                          ? "visible grid-rows-[1fr] opacity-100"
                          : "invisible grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="min-h-0 overflow-hidden">
                        <div className="pb-2">
                            <div className={area.image || area.logo ? "flex flex-col gap-8 lg:flex-row lg:items-start" : ""}>
                              <div className={area.image || area.logo ? "flex-1" : ""}>
                                {area.paragraphs.map((p, j) => (
                                  <p
                                    key={j}
                                    className="mt-4 text-sm leading-relaxed text-zinc-400 first:mt-0 md:text-base"
                                  >
                                    {p}
                                  </p>
                                ))}
                                <a
                                  href={area.anchor.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`what-i-do-link-${area.num}`}
                                  className="group/link mt-6 inline-flex items-center gap-2 text-sm font-semibold transition-colors"
                                  style={{ color: "#7FA8E8" }}
                                >
                                  {area.anchor.text}
                                  <ArrowUpRight
                                    size={16}
                                    className="transition-transform group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5"
                                  />
                                </a>
                              </div>
                              {area.image && (
                                <div className="lg:w-72 lg:shrink-0">
                                  <img
                                    src={area.image.src}
                                    alt={area.image.alt}
                                    data-testid={`what-i-do-image-${area.num}`}
                                    className="h-auto w-full rounded-lg border border-white/10 shadow-xl"
                                  />
                                </div>
                              )}
                              {area.logo && (
                                <figure className="lg:w-60 lg:shrink-0">
                                  <div className="rounded-lg bg-white p-5 shadow-xl">
                                    <img
                                      src={area.logo.src}
                                      alt={area.logo.alt}
                                      data-testid={`what-i-do-logo-${area.num}`}
                                      className="h-auto w-full"
                                    />
                                  </div>
                                  <figcaption
                                    data-testid={`what-i-do-logo-caption-${area.num}`}
                                    className="mt-3 text-center font-serif text-sm italic text-zinc-500"
                                  >
                                    {area.logo.caption}
                                  </figcaption>
                                </figure>
                              )}
                            </div>
                          </div>
                        </div>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
